import { Copy, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Form } from '@nucleus-commerce/ui/components/form';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toApiKeyCreate } from '../api/mappers';
import { useCreateApiKey } from '../hooks/use-api-key-mutations';
import {
  apiKeyFormSchema,
  emptyApiKeyForm,
  type ApiKeyFormValues,
  API_KEY_FIELDS,
  apiKeyFieldOf,
} from '../schemas/api-key-form-schema';

import { ApiKeyFields } from './api-key-fields';

function KeyPanel({ apiKey, onDone }: { apiKey: string; onDone: () => void }) {
  const { t } = useTranslation('apiKeys');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success(t('secret.copied'));
    } catch {
      toast.error(t('secret.copyFailed'));
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <code
          className="bg-muted flex-1 overflow-x-auto rounded-md px-3 py-2 font-mono text-sm select-all"
          data-testid="api-key-value"
        >
          {apiKey}
        </code>
        <Button type="button" variant="outline" onClick={() => void copy()}>
          <Copy aria-hidden />
          {t('secret.copy')}
        </Button>
      </div>
      <Alert>
        <TriangleAlert aria-hidden />
        <AlertTitle>{t('secret.warningTitle')}</AlertTitle>
        <AlertDescription>{t('secret.warning')}</AlertDescription>
      </Alert>
      <DialogFooter>
        <Button type="button" onClick={onDone}>
          {t('secret.done')}
        </Button>
      </DialogFooter>
    </div>
  );
}

function CreateForm({
  storeId,
  onCreated,
  onClose,
}: {
  storeId: string;
  onCreated: (key: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['apiKeys', 'common']);
  const message = useErrorMessage();
  const create = useCreateApiKey(storeId);
  const form = useForm<ApiKeyFormValues>({
    resolver: useI18nResolver(apiKeyFormSchema),
    defaultValues: emptyApiKeyForm(),
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      const created = await create.mutateAsync(toApiKeyCreate(values));
      onCreated(created.key);
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, {
          knownFields: API_KEY_FIELDS,
          mapField: apiKeyFieldOf,
        })
      )
        return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <ApiKeyFields form={form} />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? t('common:actions.saving') : t('apiKeys:create.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

export interface CreateApiKeyDialogProps {
  storeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Creates a key and shows it once; closing the dialog drops it for good. */
export function CreateApiKeyDialog({ storeId, open, onOpenChange }: CreateApiKeyDialogProps) {
  const { t } = useTranslation('apiKeys');
  const [apiKey, setApiKey] = useState<string | null>(null);

  const close = () => {
    onOpenChange(false);
    setApiKey(null);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent
        className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          if (apiKey) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{apiKey ? t('secret.createdTitle') : t('create.title')}</DialogTitle>
          <DialogDescription>
            {apiKey ? t('secret.createdDescription') : t('create.description')}
          </DialogDescription>
        </DialogHeader>
        {apiKey ? (
          <KeyPanel apiKey={apiKey} onDone={close} />
        ) : (
          <CreateForm storeId={storeId} onCreated={setApiKey} onClose={close} />
        )}
      </DialogContent>
    </Dialog>
  );
}
